<script setup>
/**
 * Why these figures differ from the old Admin > Summary page.
 *
 * Collapsed by default: finance reads it once to trust the numbers, not every visit.
 */
</script>

<template>
  <details :class="$style.details">
    <summary :class="$style.summary">
      Why this differs from the old Summary page
    </summary>

    <div :class="$style.body">
      <p :class="$style.lead">
        The old page and this dashboard agree on the totals. They disagree on how the money is split
        between products, because the old page sorts it with rules that miss part of the catalogue.
      </p>

      <ul :class="$style.list">
        <li>
          <strong>Food Handler was a leftover.</strong>
          The old page never classified Food Handler. It printed whatever remained after Food Manager
          and Apex were taken out, so anything it failed to recognise landed there.
        </li>
        <li>
          <strong>Online Food Manager was filed as Food Handler.</strong>
          Its Food Manager column covered category 1000 only. Online Food Manager sits in category 1300
          (packages 1210, 2014, 2016, 1410 and 3015), so that revenue fell into the leftover.
        </li>
        <li>
          <strong>Some payments were counted twice.</strong>
          A payment linked to both a registration and an order was added once for each. Here every payment
          is counted once.
        </li>
        <li>
          <strong>Apex site sales were never in the order data.</strong>
          They are shown separately, labelled as having no order record, instead of being left out.
        </li>
      </ul>

      <p :class="$style.example">
        Example, September 2026: the old page showed Food Manager 24%, Food Handler 72% and Apex 3%.
        Classified package by package the split was Food Handler 44%, Food Manager 37%, Alcohol 8%,
        Cards 7%, Allergen 2% and Harassment 1%. Collected ($455,948.65) and refunds ($4,181.35) were
        the same on both.
      </p>
    </div>
  </details>
</template>

<style module>
.details {
  background: white;
  border-radius: var(--border-radius-lg);
  box-shadow: 0 1px 2px var(--shadow-color);
  padding: 0 18px;
}
.summary {
  padding: 14px 0;
  cursor: pointer;
  font-size: var(--font-size-lg);
  font-weight: 600;
  color: var(--primary-font-dark-color);
}
.summary:hover {
  color: var(--pfs-red);
}
.body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0 0 16px;
  font-size: var(--font-size-lg);
  color: var(--primary-font-color);
  max-width: 78ch;
}
.lead,
.example {
  margin: 0;
}
.example {
  font-size: var(--font-size-md);
  color: var(--faint-color);
}
.list {
  margin: 0;
  padding-left: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
</style>
